import { faroCoreApiClient } from "@api/faro-core-api-client";
import { CompanyId, ErrorHandler, GroupId } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useEffect, useState } from "react";

interface Props extends ErrorHandler {
  /** The ID of the company to fetch the members */
  companyId?: CompanyId;

  /** The ID of the group to fetch the members for */
  groupId?: GroupId;
}

interface ReturnProps {
  /** The members of the group */
  groupMembers: FaroCoreApiTypes.AdmGroupMember[];

  /** Indicator whether the group members are currently being fetched */
  isFetchingGroupMembers: boolean;
}

/**
 * Use this hook to fetch the members of a group in a company by providing companyId and groupId.
 * Returns the fetched members and a loading flag.
 * If the loading fails, the fetched members will be empty array.
 */
export function useMembersOfGroup({
  companyId,
  groupId,
  errorHandler,
}: Props): ReturnProps {
  const [groupMembers, setGroupMembers] = useState<
    FaroCoreApiTypes.AdmGroupMember[]
  >([]);
  const [isFetchingGroupMembers, setIsFetchingGroupMembers] =
    useState<boolean>(true);

  // Fetches the group members
  useEffect(() => {
    async function fetchGroupMembers(
      companyId: CompanyId,
      groupId: GroupId
    ): Promise<void> {
      setIsFetchingGroupMembers(true);
      try {
        setGroupMembers(
          await faroCoreApiClient.admin.getGroupMembersById({
            companyId,
            groupId,
          })
        );
      } catch (error) {
        errorHandler({
          id: `use-members-of-group-${Date.now().toString()}`,
          title: "Could not load group members",
          error,
        });
        setGroupMembers([]);
      } finally {
        setIsFetchingGroupMembers(false);
      }
    }

    if (companyId && groupId) {
      void fetchGroupMembers(companyId, groupId);
    }
  }, [companyId, groupId, errorHandler]);

  if (!companyId && !groupId) {
    return { groupMembers: [], isFetchingGroupMembers: false };
  }

  return { groupMembers, isFetchingGroupMembers };
}
